// Head-term SEO hub/landing pages. Each entry renders through
// components/hub/HeadTermHub.tsx and is registered in config/sitemapUrls.ts.
// These target high-volume commercial queries (e.g. "ai cover") where the
// homepage currently under-ranks competitors like Musicfy.
//
// Copy note: never claim users can cover "any song" — only ever "any voice",
// with an explicit reminder that users are responsible for rights to audio
// they upload. This avoids implying a licence to cover copyrighted works.

export interface HubFaq {
  q: string;
  a: string;
}

export interface HubCategory {
  label: string;
  // Links to the existing /models?filter=<badge> taxonomy surface.
  filter: string;
}

export interface HubStep {
  title: string;
  body: string;
}

export interface HubRelatedLink {
  label: string;
  href: string;
}

export interface HubFeature {
  title: string;
  body: string;
}

export interface HubConfig {
  slug: string;
  h1: string;
  // Word within h1 to wrap in the lime highlighter swipe (must appear in h1).
  highlight?: string;
  title: string;
  metaDescription: string;
  heroSub: string;
  // Short supporting paragraph rendered above the voice grid.
  intro: string;
  // Name used in the SoftwareApplication JSON-LD block.
  appName: string;
  // Hero chips; defaults to the cover-volume chips in HeadTermHub.
  badges?: string[];
  // A titled grid of short paragraphs, rendered under the intro.
  features?: { heading: string; items: HubFeature[] };
  stepsHeading?: string;
  steps: HubStep[];
  faqs: HubFaq[];
  categories: HubCategory[];
  related: HubRelatedLink[];
}

// Shared category shortcuts — all map to proven /models?filter= badges
// (see config/sitemapUrls.ts modelFilterUrls).
const SHARED_CATEGORIES: HubCategory[] = [
  { label: "Music", filter: "music" },
  { label: "Anime", filter: "anime" },
  { label: "Cartoon", filter: "cartoon" },
  { label: "Gaming", filter: "gaming" },
  { label: "Korean", filter: "korean" },
  { label: "Public Figures", filter: "public figure" },
];

export const HUB_CONFIGS: Record<string, HubConfig> = {
  // Single consolidated "AI Cover" hub. H1 is "AI Cover" (the highest-volume
  // term); the <title> carries "AI Cover Generator" so one page ranks for both
  // "ai cover" and "ai cover generator" without cannibalization.
  "ai-cover": {
    slug: "ai-cover",
    h1: "AI Cover Generator",
    highlight: "Cover",
    title: "AI Cover Generator: Make AI Covers in Any Voice | Jammable",
    metaDescription:
      "Make AI covers with 100,000+ voices. Add audio, pick a voice, and get a studio-quality cover back in seconds. No experience needed.",
    heroSub:
      "Pick a voice, add audio you have the rights to, and get a cover back in seconds. Over 100,000 community voices to choose from.",
    intro:
      "Making a good cover used to take a studio and hours of mixing. Now you pick a voice, add your audio, and Jammable does the hard part for you.",
    appName: "Jammable AI Cover Generator",
    steps: [
      {
        title: "Pick a voice",
        body: "Search 100,000+ AI voices, from singers and rappers to game and cartoon characters. Or train your own.",
      },
      {
        title: "Add your audio",
        body: "Upload a file, record yourself, paste from the library, or generate an original song with AI.",
      },
      {
        title: "Generate your cover",
        body: "Hit create and Jammable produces a clean, studio-quality AI cover in seconds, ready to download and share.",
      },
    ],
    faqs: [
      {
        q: "What is an AI cover?",
        a: "An AI cover is a version of a track performed in a different voice using AI. You take a track (or your own vocals) and convert the vocals into another voice, like an artist, a character, or a voice you trained yourself, while keeping the melody and lyrics.",
      },
      {
        q: "How do I make an AI cover?",
        a: "Pick a voice from Jammable's library, add your audio (upload a file, record, or choose from the library), and press generate. Jammable separates the vocals, converts them to your chosen voice, and mixes the result into a finished cover, usually in under a minute. Only use audio you have the rights to use.",
      },
      {
        q: "How much does the AI cover generator cost?",
        a: "Jammable runs on subscription plans. A plan gives you AI covers with higher-quality output, faster processing, and the ability to train your own voices. Check the pricing page for current options.",
      },
      {
        q: "What is the best AI cover generator?",
        a: "Jammable is one of the most popular AI cover generators, with over 100,000 community voices, fast generation and studio-quality output. New voices are added every day, so you can create covers in almost any voice.",
      },
      {
        q: "Can I use any voice for my AI cover?",
        a: "Yes. Browse 100,000+ ready-made voices across music, anime, gaming, cartoons and more, or train your own voice model and use it on the audio you upload.",
      },
      {
        q: "Are AI covers good quality?",
        a: "Modern AI covers can sound remarkably close to a real recording. Jammable uses high-quality voice conversion and automatic mixing, and higher-quality settings on paid plans give even cleaner, more natural results.",
      },
    ],
    categories: SHARED_CATEGORIES,
    related: [
      { label: "AI Voice Changer", href: "/ai-voice-changer" },
      { label: "Browse all AI voices", href: "/models" },
      { label: "Train your own voice", href: "/train" },
    ],
  },

  "ai-voice-changer": {
    slug: "ai-voice-changer",
    h1: "AI Voice Changer",
    highlight: "Voice",
    title: "AI Voice Changer: Change Your Voice Into Anyone | Jammable",
    metaDescription:
      "Change your voice into any character or voice with Jammable's AI voice changer. 100,000+ voices, realistic results. Upload or record and convert.",
    heroSub:
      "Change your voice into anyone. Record or upload a clip, pick from 100,000+ AI voices, and hear yourself transformed in seconds.",
    intro:
      "Record a clip or upload one, choose who you want to sound like, and Jammable converts it. It works with game and cartoon characters, singers, or a voice you've trained yourself.",
    appName: "Jammable AI Voice Changer",
    steps: [
      {
        title: "Record or upload",
        body: "Record yourself with your mic or upload an audio clip you want to transform.",
      },
      {
        title: "Pick a voice",
        body: "Choose from 100,000+ AI voices, from game and cartoon characters to singers. Or train your own.",
      },
      {
        title: "Convert your voice",
        body: "Press create and Jammable converts your audio into the chosen voice in seconds, ready to download.",
      },
    ],
    faqs: [
      {
        q: "What is an AI voice changer?",
        a: "An AI voice changer takes your recorded audio and converts it so it sounds like a different voice, using AI voice conversion. Jammable can transform your vocals into any of 100,000+ voices.",
      },
      {
        q: "How do I change my voice with AI?",
        a: "Record or upload a clip, choose the voice you want to become, and press generate. Jammable converts your audio into that voice and returns a clean result you can download.",
      },
      {
        q: "How much does the AI voice changer cost?",
        a: "Jammable runs on subscription plans. A plan gives you voice conversions with higher quality, faster processing, and custom voice training. Check the pricing page for current options.",
      },
      {
        q: "Can I make a voice changer of my favourite character?",
        a: "Jammable has a huge library of character, game and cartoon voices, and new ones are added daily. You can also train a custom voice from your own audio.",
      },
      {
        q: "Does the AI voice changer work in real time?",
        a: "Jammable converts recorded or uploaded audio rather than live microphone input, which produces much higher-quality, more realistic results than real-time changers.",
      },
    ],
    categories: SHARED_CATEGORIES,
    related: [
      { label: "AI Cover Generator", href: "/ai-cover" },
      { label: "Browse all AI voices", href: "/models" },
      { label: "Train your own voice", href: "/train" },
    ],
  },
  // Weights (weights.gg, then weights.com) joined OpenAI and wound down its
  // products in 2026. In the 90 days to 2026-10-07 we got 9,372 clicks from
  // "weights" searches, and 9,230 of them landed on one user-uploaded voice
  // (/custom-weightsgg-male-announcer). This page gives that demand a home
  // that does not depend on one user keeping a voice public. Keep claims
  // factual: we are not affiliated with Weights; quote what they announced.
  "weights-gg-alternative": {
    slug: "weights-gg-alternative",
    h1: "The Weights.gg Alternative",
    highlight: "Alternative",
    title: "Weights.gg Alternative: AI Voices, Covers & RVC Models | Jammable",
    metaDescription:
      "Weights has shut down. Jammable has 100,000+ community AI voices, AI covers, text-to-speech, voice training and RVC model uploads. Bring your .pth files.",
    heroSub:
      "Weights has wound down its products. Jammable does what you used it for: 100,000+ community voices, AI covers, text-to-speech, and training or uploading your own RVC models.",
    badges: ["100,000+ voices", "Upload .pth models", "Text-to-speech"],
    intro:
      "Weights announced that its team is joining OpenAI and that its products are no longer available. If you made AI covers, used community voice models or trained your own voice on Weights, all of that works on Jammable today. Jammable is not affiliated with Weights.",
    appName: "Jammable",
    features: {
      heading: "Everything you used Weights for",
      items: [
        {
          title: "Community AI voices",
          body: "Over 100,000 voice models made by the community: singers, rappers, game and cartoon characters, streamers and announcers. New voices arrive every day.",
        },
        {
          title: "AI covers",
          body: "Pick a voice, add audio you have the rights to, and get a mixed cover back in about a minute. Jammable separates the vocals, converts them and mixes them back for you.",
        },
        {
          title: "Upload your RVC models",
          body: "Kept your model files? Upload the .pth and .index files from your Library and use the voice straight away. Share it publicly or keep it to yourself.",
        },
        {
          title: "Train a new voice",
          body: "Lost your files? Train the voice again from at least 6 minutes of clean singing or speech, and use it for covers and text-to-speech.",
        },
        {
          title: "Text-to-speech",
          body: "Type up to 2,500 characters and hear them in any voice in the library, with accent, expression and speed settings.",
        },
        {
          title: "API and AI agents",
          body: "Build on it: a REST API for covers and text-to-speech, plus an MCP server so Claude, Cursor and other agents can use Jammable directly.",
        },
      ],
    },
    stepsHeading: "Move from Weights in three steps",
    steps: [
      {
        title: "Find your voices",
        body: "Search the library for the voices you used. Most popular community voices already have versions on Jammable.",
      },
      {
        title: "Bring your own models",
        body: "Have your .pth and .index files? Open your Library and choose Upload voice. If not, train the voice again from your recordings.",
      },
      {
        title: "Make covers again",
        body: "Add your audio, pick the voice and create. Download your cover, share it, or keep it in your Library.",
      },
    ],
    faqs: [
      {
        q: "Is Weights.gg shut down?",
        a: "Yes. Weights announced that its team is joining OpenAI and that its products and services have been wound down and are no longer available. weights.gg now redirects to that announcement.",
      },
      {
        q: "What is the best alternative to Weights.gg?",
        a: "Jammable covers the same ground: a large library of community AI voices, AI covers, text-to-speech, voice training, and uploading your own RVC models. It has more than 100,000 voices, with new ones every day.",
      },
      {
        q: "Can I upload my Weights voice models to Jammable?",
        a: "Yes, if you kept the model files. Jammable accepts RVC models as a .pth file plus its .index file. Open your Library, choose Upload voice, add both files, a name and an image, and the voice is ready to use. Only upload models you made or have permission to share.",
      },
      {
        q: "I lost my model files. Can I get my voice back?",
        a: "You can train it again on Jammable from the original recordings. You need at least 6 minutes of clean singing or speech, without silence.",
      },
      {
        q: "Is Jammable free like Weights was?",
        a: "Browsing every voice and listening to demo covers is free. Making covers, training voices and text-to-speech need a plan. See the pricing page for current options.",
      },
      {
        q: "Is Jammable affiliated with Weights?",
        a: "No. Jammable is an independent AI voice platform with no connection to Weights or OpenAI.",
      },
    ],
    categories: SHARED_CATEGORIES,
    related: [
      { label: "AI Cover Generator", href: "/ai-cover" },
      { label: "Train your own voice", href: "/train" },
      { label: "Browse all AI voices", href: "/models" },
      { label: "Jammable API for developers", href: "/developers" },
    ],
  },
};

export const HUB_SLUGS = Object.keys(HUB_CONFIGS);
